'use client';

import {
  type Edge,
  type Node,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AI_FLOW_GRAPH_COLORS } from './constants';
import {
  AiFlowCustomNode,
  type AiFlowCustomNodeProps,
} from './AiFlowCustomNode';
import { AiFlowCustomEdge } from './AiFlowCustomEdge';
import {
  getAiFlowNodeColor,
  getAiFlowEdgeOpacity,
  getAiFlowNodeOpacity,
  getAiFlowNodeLabel,
} from './utils';
import type { AiFlowNodeCategory } from './types';
import type { AiFlowGraphReturnDto } from '@terra/shared-contracts-git';
import { FlowGraphBase } from '../flow-graph-base/FlowGraphBase';
import {
  NodeDetailsPopover,
  type RepositoryInfo,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { Button } from '@terra/ui-design-system';

type AiFlowNode = Node<AiFlowCustomNodeProps>;

export type AiFlowGraphNodeProps = AiFlowNode;

export type AiFlowGraphProps = {
  data: AiFlowGraphReturnDto;
  codeGraphNodeIds: Set<string>;
  repositoryInfoMap?: Record<string, RepositoryInfo>;
  nodeToCodeGraphMap?: Map<string, string>;
  onViewRelatedCodeGraph?: (
    codeGraphId: string,
    nodeType: AiFlowNodeCategory,
  ) => void;
  width?: number;
  height?: number;
  onNodeClick?: (node: AiFlowGraphNodeProps) => void;
};

export function AiFlowGraph({
  data,
  codeGraphNodeIds,
  repositoryInfoMap = {},
  nodeToCodeGraphMap = new Map(),
  onViewRelatedCodeGraph,
  width,
  height,
  onNodeClick,
}: AiFlowGraphProps) {
  const t = useTranslations('designSystem.codeGraph.nodeDetailsPopover');
  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNode, setSelectedNode] = useState<AiFlowNode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hadNonZeroSizeRef = useRef(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width: w, height: h } = entry.contentRect;
      if (w > 0 && h > 0 && !hadNonZeroSizeRef.current) {
        hadNonZeroSizeRef.current = true;
        setTimeout(() => fitView({ duration: 0, padding: 0.1 }), 100);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [fitView]);

  const nodeTypes = useMemo(() => ({ custom: AiFlowCustomNode }), []);
  const edgeTypes = useMemo(() => ({ custom: AiFlowCustomEdge }), []);

  useEffect(() => {
    if (!data?.nodes?.length) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const flowNodes: AiFlowNode[] = data.nodes.map((node) => ({
      id: node.id,
      type: 'custom' as const,
      position: { x: 0, y: 0 },
      data: {
        type: node.type,
        name: node.name,
        filePath: node.filePath,
        startLine: node.startLine,
        endLine: node.endLine,
        repositoryName: node.repositoryName,
        id: `ai-flow-node-${node.id}`,
        hasCodeGraph: false,
        framework: node.framework,
      },
    }));

    const flowEdges: Edge[] = data.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label ?? edge.type,
      type: 'custom' as const,
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [data, setEdges, setNodes]);

  useEffect(() => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          hasCodeGraph: codeGraphNodeIds.has(node.id),
        },
      })),
    );
  }, [codeGraphNodeIds, setNodes]);

  useEffect(() => {
    const selectedNodeId = selectedNode?.id ?? null;
    setNodes((currentNodes) =>
      currentNodes.map((node) => ({
        ...node,
        style: {
          ...node.style,
          opacity: getAiFlowNodeOpacity(node.id, selectedNodeId ?? undefined),
          transition: 'opacity 0.3s ease',
        },
      })),
    );
    setEdges((currentEdges) =>
      currentEdges.map((edge) => ({
        ...edge,
        style: {
          ...edge.style,
          opacity: getAiFlowEdgeOpacity(
            edge.source,
            edge.target,
            selectedNodeId ?? undefined,
          ),
          transition: 'opacity 0.3s ease',
        },
      })),
    );
  }, [selectedNode, setNodes, setEdges]);

  const onGetNodeColor = useCallback((node: Node) => {
    return getAiFlowNodeColor((node as AiFlowNode).data.type);
  }, []);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setSelectedNode((prev) =>
        prev?.id === node.id ? null : (node as AiFlowNode),
      );
      onNodeClick?.(node as AiFlowNode);
    },
    [onNodeClick],
  );

  const handlePaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleClosePopover = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const relatedCodeGraphId = selectedNode
    ? (nodeToCodeGraphMap.get(selectedNode.id) ?? null)
    : null;

  return (
    <FlowGraphBase
      ref={containerRef}
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodeClick={handleNodeClick}
      onPaneClick={handlePaneClick}
      onGetNodeColor={onGetNodeColor}
      backgroundColor={AI_FLOW_GRAPH_COLORS.background}
      minZoom={0.3}
      maxZoom={5}
      width={width}
      height={height}
    >
      {selectedNode && (
        <NodeDetailsPopover
          isOpen
          onClose={handleClosePopover}
          badgeText={getAiFlowNodeLabel(selectedNode.data.type)}
          filePath={selectedNode.data.filePath}
          startLine={selectedNode.data.startLine ?? undefined}
          endLine={selectedNode.data.endLine ?? undefined}
          repositoryInfo={repositoryInfoMap[selectedNode.data.repositoryName]}
        >
          {relatedCodeGraphId && onViewRelatedCodeGraph && (
            <Button
              size="xs"
              variant="ghost"
              w="full"
              borderTopWidth="1px"
              borderTopColor="border.subtle"
              borderRadius="0 0 md md"
              onClick={(e) => {
                e.stopPropagation();
                onViewRelatedCodeGraph(
                  relatedCodeGraphId,
                  selectedNode.data.type,
                );
                setSelectedNode(null);
              }}
            >
              {t('viewRelatedCodeGraph')}
            </Button>
          )}
        </NodeDetailsPopover>
      )}
    </FlowGraphBase>
  );
}
